import {useContext, useEffect, useState} from "react";
import {ContentCopyRounded, DeleteRounded, ExpandMore, Person, SearchRounded} from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	IconButton,
	Stack,
	Tab,
	Tabs,
	Typography
} from "@mui/material";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useFormContext, useWatch} from "react-hook-form";
import {useRecoilState, useRecoilValue} from "recoil";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {IGetCustomersParams} from "../../../../booking/api/services";
import {BILLING_OWNER_TYPE, COMPANY_CODES, LOCALE_DATE_FORMAT} from "../../../config";
import {ICustomer, IGenericOption, IRaFillingForm} from "../../../models/models";
import SearchCustomerDialog from "../../search-customer-dialog/SearchCustomerDialog";
import {bookingDataValueState, raFillingAccordionValidationState} from "../../state";
import useDialog from "../../use-dialog/UseDialog";
import BillingCompanyData from "./billing-company-data/BillingCompanyData";
import BillingPersonData from "./billing-person-data/BillingPersonData";
import {CREDIT_TYPE_VALUES, INVOICE_TYPES, RA_FILLING_FORM_INITIAL_VALUE, SEGMENT_VALUES} from "../config";
import {CustomerSearchFilters} from "../../../types";
import {findCustomerBaseByUniqueData} from "../../../check-out-in/services/http.services";
import CustomLoader from "../../../../../util/component/CustomLoader";
import {isImmutableRa, isRetail} from "../../../utils";
import {CUSTOMER_STATES} from "../../config";
import {mapRaOwnerToPersonBillingOwner, resetFieldBO} from "../../services/utils";
import {deepClone} from "../../../../../util/state/commonFunctions";

/*
NOTE per la gestione di BILLING OWNER
curBillingOwner -> bookingEntity.billingOwner || bookingEntity.secondBillingOwner
						(bookingEntity is retriveDraftRaQuery && updateDraftRaMutation response)

{creditType, firstInvoiceRecipient, secondInvoiceRecipient} = bookingEntity.financialInfo

invoiceRecipient -> (bookingEntity?.billingOwner?.billingOwnerType ?? firstInvoiceRecipient ) ||
					(bookingEntity?.secondBillingOwner?.billingOwnerType ?? secondInvoiceRecipient)

// tutte ret sono no_credit
 */

/**
 * BillingOwnerInfo
 * @param boType
 * @constructor
 */
const BillingOwnerInfo = ({boType, keyless}: { boType: 'first' | 'second', keyless: boolean | undefined }) => {
    const {dispatchGlobalData} = useContext(GlobalContext);
    const {t} = useTranslation(['network', 'translation']);

    const raFillingDataForm = useFormContext<IRaFillingForm>();
    const bookingDataValue = useRecoilValue(bookingDataValueState);
    const [disableTab, setDisableTab] = useState<[boolean, boolean, boolean]>([false, false, false]);
    const [editableTab, setEditableTab] = useState([true, true, true]);
    const [enabledActions, setEnabledActions] = useState([false, true, true]);
    const [disabledCopyActionPersonBA, setDisabledCopyActionPersonBA] = useState(false);
    const [firstBillingOwnerFilter, setFirstBillingOwnerFilter] = useState<IGetCustomersParams['filters']>(null);
    const [dsetSearch, setDSetSearch] = useState<ICustomer[] | undefined>(undefined);
    const [searchMessage, setSearchMessage] = useState<string>('');
    const [absenceDataFlag, setAbsenceDataFlag] = useState<boolean>(false);
    const [raFillingAccordionValidation, setRaFillingAccordionValidation] = useRecoilState(raFillingAccordionValidationState);

    const typeBO: ['person', 'vatNumber', 'company'] = ['person', 'vatNumber', 'company'];
    const {
        creditType,
        firstInvoiceRecipient,
        secondInvoiceRecipient,
        segment,
        paymentType
    } = bookingDataValue.financialInfo;
    const immutableRa = isImmutableRa(segment, paymentType);
    let invoiceRecipient = boType === 'first' ? firstInvoiceRecipient : secondInvoiceRecipient;
    const hasSecondBo = isRetail(bookingDataValue?.bookingDetail?.rentalType) || [CREDIT_TYPE_VALUES.PARTIAL_CREDIT].includes(creditType);

    const [firstBillingOwnerPersonCustomerId, firstBillingOwnerVatNumberHolderCustomerId,
        firstBillingOwnerCompanyHolderCustomerId, billingOwnerActiveTab, billingOwnerSecondActiveTab] =
        useWatch<IRaFillingForm>({
            name: [
                `billingOwnerInfo.${boType}.person.customerId`,
                `billingOwnerInfo.${boType}.vatNumber.customerId`,
                `billingOwnerInfo.${boType}.company.customerId`,
                `billingOwnerInfo.billingOwnerActiveTab`,
                `billingOwnerInfo.billingOwnerSecondActiveTab`
            ]
        });

    const boActiveTab = boType === 'first' ? 'billingOwnerActiveTab' : 'billingOwnerSecondActiveTab';
    const boActiveTabValue = boType === 'first' ? billingOwnerActiveTab : billingOwnerSecondActiveTab;

    const curCustomerId = [firstBillingOwnerPersonCustomerId, firstBillingOwnerVatNumberHolderCustomerId, firstBillingOwnerCompanyHolderCustomerId][boActiveTabValue];

    const queryClient = useQueryClient();
    const handleChangeActiveTab = (e: React.SyntheticEvent, activeTab: 0 | 1 | 2) => {
        e.stopPropagation();
        raFillingDataForm.setValue(`billingOwnerInfo.${boActiveTab}`, activeTab);
    };

    const firstBillingOwnerSearchDialog = useDialog();

    const handleBillingOwnerResetTabFields = (_e: React.SyntheticEvent, billingOwner: 'first' | 'second') => {
        const curType = typeBO[boActiveTabValue];
        if (curType === 'person') {
            raFillingDataForm.setValue(`billingOwnerInfo.${billingOwner}.person.cfValidated`, false);
        }
        resetFieldBO(deepClone(RA_FILLING_FORM_INITIAL_VALUE.billingOwnerInfo[billingOwner][curType]), boActiveTabValue, billingOwner, raFillingDataForm);
        if (curType === 'vatNumber') {
            const [[_, legalEntities]] = queryClient.getQueriesData<IGenericOption[]>(['ntw_legal_entities']);
            raFillingDataForm.setValue(`billingOwnerInfo.${billingOwner}.vatNumber.legalEntity`, legalEntities?.find(({code}) => code === COMPANY_CODES.DITTA_INDIVIDUALE))
        }
    }

    const setDriverData = (type: string, val: ICustomer | any) => {
        for (const [key, value] of Object.entries(val)) {
            //@ts-ignore
            raFillingDataForm.setValue(`${type}.${key}`, value);
        }
    }

    const handleFirstBillingOwnerCopyClick = () => {
        if (boActiveTabValue === 0) {
            setDriverData(`billingOwnerInfo.${boType}.person`, mapRaOwnerToPersonBillingOwner(bookingDataValue.raOwner));
        }
    }

    const findCustomerByUniqueDataQuery = useMutation({
        mutationFn: (queryBody: CustomerSearchFilters) => findCustomerBaseByUniqueData(queryBody),
        onSuccess: (resp: any) => {
            const respData: ICustomer[] = resp?.data;
            if (!!respData && respData.length > 0) {
                switch (boActiveTabValue) {
                    case 0:
                        setDSetSearch(respData);
                        firstBillingOwnerSearchDialog.handleShow();
                        break;
                    case 1:
                        if (respData.some((r) => r?.legalEntity?.code === COMPANY_CODES.DITTA_INDIVIDUALE)) {
                            setDSetSearch(respData)
                            firstBillingOwnerSearchDialog.handleShow();
                            return;
                        }

                        setSearchMessage('raFilling.billingOwnerInfo.message.info.isCompany')
                        break;
                    case 2:
                        if (respData.some((r) => r?.legalEntity?.code !== COMPANY_CODES.DITTA_INDIVIDUALE)) {
                            setDSetSearch(respData)
                            firstBillingOwnerSearchDialog.handleShow();
                            return;
                        }

                        setSearchMessage('raFilling.billingOwnerInfo.message.info.isVatNumber')
                        break;
                }
                return;
            }
            setSearchMessage('raFilling.billingOwnerInfo.message.info.noUser')
        },
        onError: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('raFilling.billingOwnerInfo.message.error.userSearch'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            })
        }
    });

    const closeFirstBOSDialog = () => {
        setDSetSearch(undefined);
        firstBillingOwnerSearchDialog.handleClose()
    }

    useEffect(() => {
        if (searchMessage !== '') {
            setTimeout(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t(searchMessage),
                            type: snackbarTypes.info,
                            show: true,
                            autoClose: true,
                        }
                    }
                })
            }, 1500)
        }
    }, [searchMessage]);

    useEffect(() => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('raFilling.billingOwnerInfo.message.loading.searchingUser'),
                    type: snackbarTypes.info,
                    show: findCustomerByUniqueDataQuery.isLoading,
                    autoClose: false,
                }
            }
        })
    }, [findCustomerByUniqueDataQuery.isLoading])

    const handleFirstBillingOwnerSearchClick = () => {
        if (boActiveTabValue === 0) {
            const firstBillingOwnerPerson = raFillingDataForm.getValues(`billingOwnerInfo.${boType}.person`);
            if (!firstBillingOwnerPerson || !firstBillingOwnerPerson.firstName || !firstBillingOwnerPerson.lastName) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('raFilling.billingOwnerInfo.message.error.billingOwnerSearchRequiredFields'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                })
                return;
            }
            setFirstBillingOwnerFilter({
                firstName: firstBillingOwnerPerson?.firstName,
                lastName: firstBillingOwnerPerson?.lastName,
                birthDate: firstBillingOwnerPerson?.birthDate?.format(LOCALE_DATE_FORMAT)
            })
        } else if (boActiveTabValue === 1) {
            const firstBillingOwnerVatNumberHolder = raFillingDataForm.getValues(`billingOwnerInfo.${boType}.vatNumber`);
            setFirstBillingOwnerFilter({
                companyName: firstBillingOwnerVatNumberHolder?.companyName || undefined,
				vatNumber: firstBillingOwnerVatNumberHolder?.VATNumber || undefined,
				//siren: firstBillingOwnerVatNumberHolder?.siren || undefined,
				//siret: firstBillingOwnerVatNumberHolder?.siret || undefined,
            })
        } else if (boActiveTabValue === 2) {
            const firstBillingOwnerCompanyHolder = raFillingDataForm.getValues(`billingOwnerInfo.${boType}.company`);
            setFirstBillingOwnerFilter({
                companyName: firstBillingOwnerCompanyHolder?.companyName || undefined,
				vatNumber: firstBillingOwnerCompanyHolder?.VATNumber || undefined,
				//siren: firstBillingOwnerCompanyHolder?.siren || undefined,
				//siret: firstBillingOwnerCompanyHolder?.siret || undefined,
            })
        }
        firstBillingOwnerSearchDialog.handleShow();
    }

    const handleBillingOwnerSelected = (customer: ICustomer) => {
        if (customer?.state === CUSTOMER_STATES.SUSPENDED) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`customerData.message.error.suspended_${boType}_bo`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
            return false;
        }

        const billingOwnerInfoDefaultValue = deepClone(raFillingDataForm.formState.defaultValues?.billingOwnerInfo);
        // @ts-ignore
        billingOwnerInfoDefaultValue.billingOwnerActiveTab = billingOwnerActiveTab;
        // @ts-ignore
        billingOwnerInfoDefaultValue.billingOwnerSecondActiveTab = billingOwnerSecondActiveTab;

        switch (boActiveTabValue) {
            case 0:
                resetFieldBO(customer, boActiveTabValue, boType, raFillingDataForm);
                return false;
            case 1:
                if (customer?.legalEntity?.code === COMPANY_CODES.DITTA_INDIVIDUALE) {
                    resetFieldBO(customer, boActiveTabValue, boType, raFillingDataForm);
                    return false;
                }

                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('raFilling.billingOwnerInfo.message.info.individualSocietySelected'),
                            type: snackbarTypes.info,
                            show: true,
                            autoClose: true,
                        }
                    }
                })

                return true;
            case 2:
                if (customer?.legalEntity?.code !== COMPANY_CODES.DITTA_INDIVIDUALE) {
                    resetFieldBO(customer, boActiveTabValue, boType, raFillingDataForm);
                    return false;
                }

                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('raFilling.billingOwnerInfo.message.info.individualSocietyNotSelected'),
                            type: snackbarTypes.info,
                            show: true,
                            autoClose: true,
                        }
                    }
                })
                return true;
        }
    }

    const setBOEditableState = () => {
        let curBillingOwner = bookingDataValue.billingOwner;

        const isBaVatNumberHolder = curBillingOwner?.legalEntity?.code === COMPANY_CODES.DITTA_INDIVIDUALE;
        const isBaPerson = !curBillingOwner?.legalEntity;
        const isRaPrepaid = segment === SEGMENT_VALUES.SUBSCRIPTION || (segment === SEGMENT_VALUES.RETAIL && bookingDataValue.financialInfo.paymentTiming === '1');
        const isRaPoa = segment === SEGMENT_VALUES.RETAIL && bookingDataValue.financialInfo.paymentTiming === '2';
        const isRaTourOperator = segment === SEGMENT_VALUES.TOUR_OPERATOR;
        let localCreditType = creditType;
        if (!localCreditType) {
            localCreditType = CREDIT_TYPE_VALUES.NO_CREDIT;
        }

        const disableAll = () => {
            setDisableTab([true, true, true]);
            setEditableTab([false, false, false]);
            setEnabledActions([false, false, false]);
        };

        const enableAll = () => {
            setDisableTab([false, false, false]);
            setEditableTab([true, true, true]);
            setEnabledActions([true, true, true]);
        }

        switch (localCreditType) {
            case CREDIT_TYPE_VALUES.PUBLIC:
                setDisableTab([false, false, false]);
                setEditableTab([false, true, true]);
                setEnabledActions([false, true, true]);
                break;

            case CREDIT_TYPE_VALUES.FULL_CREDIT: //full credit has only first bo
                if (isBaPerson) {
                    disableAll();
                    setDisabledCopyActionPersonBA(true);
                } else if (isBaVatNumberHolder) {
                    setDisableTab([true, false, true]);
                    setEditableTab([false, false, false]);
                    setEnabledActions([false, false, false]);
                } else { //is company case
                    setDisableTab([true, true, false]);
                    setEditableTab([false, false, false]);
                    setEnabledActions([false, false, false]);
                }
                break;

            case CREDIT_TYPE_VALUES.PARTIAL_CREDIT:
                if (boType === 'first') {
                    if (isBaPerson) {
                        disableAll();
                        setDisabledCopyActionPersonBA(true);
                    } else if (isBaVatNumberHolder) {
                        setDisableTab([true, false, true]);
                        setEditableTab([false, false, false]);
                        setEnabledActions([false, false, false]);
                    } else { //is company case
                        setDisableTab([true, true, false]);
                        setEditableTab([false, false, false]);
                        setEnabledActions([false, false, false]);
                    }
                } else if (boType === 'second') {
                    if (isBaPerson) {
                        setDisableTab([false, true, true]);
                        setEditableTab([false, false, false]);
                        setEnabledActions([false, false, false]);
                    } else if (isBaVatNumberHolder) {
                        setDisableTab([false, false, false]);
                        setEditableTab([false, true, true]);
                        setEnabledActions([false, true, true]);
                    } else { //is company case
                        setDisableTab([false, false, false]);
                        setEditableTab([false, true, true]);
                        setEnabledActions([false, true, true]);
                    }
                }
                break;

            case CREDIT_TYPE_VALUES.NO_CREDIT: //no credit has only first bo or first and second has the same logic
                if (isRaPrepaid) {
                    disableAll();
                    setDisabledCopyActionPersonBA(true);
                } else if (isRaPoa) {
                    enableAll();
                } else if (isRaTourOperator) {
                    setDisableTab([false, false, false]);
                    setEditableTab([false, true, true]);
                    setEnabledActions([false, true, true]);
                } else {
                    if (isBaPerson) {
                        disableAll();
                        setDisabledCopyActionPersonBA(true);
                    } else if (isBaVatNumberHolder) {
                        setDisableTab([false, false, false]);
                        setEditableTab([false, true, true]);
                        setEnabledActions([false, true, true]);
                    } else { //is company case
                        setDisableTab([false, false, false]);
                        setEditableTab([false, true, true]);
                        setEnabledActions([false, true, true]);
                    }
                }
                break;
        }

        if (bookingDataValue.bookingDetail.flagSubstitution) {
            disableAll();
        }
    };

    useEffect(() => {
        if (boType === 'first' || (hasSecondBo && boType === 'second')) {
            setBOEditableState();
        }
    }, [boType])

    useEffect(() => {
        // check absenceof data
        if (!!bookingDataValue.bookingDetail.flagSubstitution) {
            let outcome = false;
            const type = typeBO[boActiveTabValue];
            const billingOwner = raFillingDataForm.getValues(`billingOwnerInfo.${boType}.${type}`);
            if (!!billingOwner) {
                for (const [key, value] of Object.entries(billingOwner)) {
                    if (!value) {
                        outcome = true;
                        break;
                    }
                }
            }

            setAbsenceDataFlag(boActiveTabValue);
        }
    }, [boActiveTabValue])

    const handleAccordionOnChange = (e: React.SyntheticEvent, expanded: boolean) => {
        e.stopPropagation();

        setRaFillingAccordionValidation((prev) => {
            return {...prev, [boType === 'first' ? "firstBillingOwnerInfo" : "secondBillingOwnerInfo"]: expanded};
        })
    }

    return (
        <>
            <Accordion
                expanded={raFillingAccordionValidation[boType === 'first' ? "firstBillingOwnerInfo" : "secondBillingOwnerInfo"]}
                onChange={(e, expanded) => handleAccordionOnChange(e, expanded)}>
                <AccordionSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="h6">
                        {t(`raFilling.billingOwnerInfo.accordion.title.${boType}`)}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Box sx={{width: '100%', mb: 2}}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Stack direction="row" alignItems="center" columnGap={2}>
                                <Box>
                                    <Tabs value={boActiveTabValue} onChange={handleChangeActiveTab}>
                                        <Tab label={t("raFilling.billingOwnerInfo.tab.person")} value={0}
                                             disabled={immutableRa || disableTab[0] || bookingDataValue.bookingDetail.flagSubstitution}/>
                                        <Tab label={t("raFilling.billingOwnerInfo.tab.vat")} value={1}
                                             disabled={immutableRa || disableTab[1] || bookingDataValue.bookingDetail.flagSubstitution}/>
                                        <Tab label={t("raFilling.billingOwnerInfo.tab.company")} value={2}
                                             disabled={immutableRa || disableTab[2] || bookingDataValue.bookingDetail.flagSubstitution}/>
                                    </Tabs>
                                </Box>
                                {curCustomerId &&
                                    <Link to={`/customerbase/view/${curCustomerId}`} target="_blank">
                                        <Person color="secondary"/>
                                    </Link>
                                }
                            </Stack>

                            {
                                (!bookingDataValue.bookingDetail.flagSubstitution || absenceDataFlag) &&
                                <>
                                    <Stack direction="row" alignItems="center">
                                        <IconButton
                                            onClick={handleFirstBillingOwnerSearchClick}
                                            title={t("raFilling.billingOwnerInfo.button.searchBillingOwner")}
                                            disabled={immutableRa || !enabledActions[boActiveTabValue]}
                                        >
                                            <SearchRounded/>
                                        </IconButton>
                                        <IconButton
                                            onClick={(e: React.SyntheticEvent) => handleBillingOwnerResetTabFields(e, boType)}
                                            title={t("raFilling.billingOwnerInfo.button.clearBillingOwner")}
                                            disabled={immutableRa || !enabledActions[boActiveTabValue]}
                                        >
                                            <DeleteRounded/>
                                        </IconButton>
                                        <IconButton
                                            onClick={handleFirstBillingOwnerCopyClick}
                                            title={t("raFilling.billingOwnerInfo.button.copyBillingOwner")}
                                            disabled={boActiveTabValue !== 0 || disabledCopyActionPersonBA}
                                        >
                                            <ContentCopyRounded/>
                                        </IconButton>
                                    </Stack>
                                </>
                            }
                        </Stack>

                        <Box role="tabpanel">
                            {boActiveTabValue === 0 &&
                                <BillingPersonData
                                    type={boType}
                                    editable={!immutableRa && editableTab[0]}
                                    findCustomerByUniqueDataQuery={findCustomerByUniqueDataQuery}
                                    forceReadOnly={!!keyless}
                                />
                            }
                            {boActiveTabValue === 1 &&
                                <BillingCompanyData
                                    type={boType}
                                    editable={!immutableRa && editableTab[1]}
                                    findCustomerByUniqueDataQuery={findCustomerByUniqueDataQuery}
                                    curLegalForm="vatNumber"
                                    forceReadOnly={!!keyless}
                                />
                            }
                            {boActiveTabValue === 2 &&
                                <BillingCompanyData
                                    type={boType}
                                    editable={!immutableRa && editableTab[2]}
                                    findCustomerByUniqueDataQuery={findCustomerByUniqueDataQuery}
                                    curLegalForm="company"
                                    forceReadOnly={!!keyless}
                                />
                            }
                        </Box>
                    </Box>
                    <CustomLoader loading={findCustomerByUniqueDataQuery.isLoading}/>
                    <SearchCustomerDialog
                        type={boActiveTabValue === 0 ? BILLING_OWNER_TYPE.PERSON : BILLING_OWNER_TYPE.COMPANY}
                        open={firstBillingOwnerSearchDialog.open}
                        onClose={closeFirstBOSDialog}
                        customersFilter={firstBillingOwnerFilter}
                        onCustomerSelected={handleBillingOwnerSelected}
                        customerList={dsetSearch}
                    />
                </AccordionDetails>
            </Accordion>
        </>
    );
}

export default BillingOwnerInfo;
